/**
 * Color Extraction Utility
 * 
 * Extracts dominant colors from an image using Canvas API
 * with k-means clustering algorithm for better color selection.
 */

interface RGB {
    r: number;
    g: number;
    b: number;
}

interface ColorResult {
    hex: string;
    rgb: RGB;
    count: number;
}

/**
 * Convert RGB to Hex color string
 */
function rgbToHex(r: number, g: number, b: number): string {
    return '#' + [r, g, b].map(x => {
        const hex = Math.round(x).toString(16);
        return hex.length === 1 ? '0' + hex : hex;
    }).join('').toUpperCase();
}

/**
 * Calculate color distance (Euclidean)
 */
function colorDistance(c1: RGB, c2: RGB): number {
    return Math.sqrt(
        Math.pow(c1.r - c2.r, 2) +
        Math.pow(c1.g - c2.g, 2) +
        Math.pow(c1.b - c2.b, 2)
    );
}

/**
 * Check if a color is too close to white, black, or gray
 */
function isNeutralColor(color: RGB, threshold: number = 30): boolean {
    const isNearWhite = color.r > 225 && color.g > 225 && color.b > 225;
    const isNearBlack = color.r < 30 && color.g < 30 && color.b < 30;
    const isGrayish =
        Math.abs(color.r - color.g) < threshold &&
        Math.abs(color.g - color.b) < threshold &&
        Math.abs(color.r - color.b) < threshold;

    return isNearWhite || isNearBlack || isGrayish;
}

/**
 * K-means clustering for color grouping
 */
function kMeansClustering(colors: RGB[], k: number, iterations: number = 10): RGB[] {
    if (colors.length <= k) return colors;

    // Initialize centroids randomly
    let centroids: RGB[] = [];
    const shuffled = [...colors].sort(() => Math.random() - 0.5);
    for (let i = 0; i < k && i < shuffled.length; i++) {
        centroids.push({ ...shuffled[i] });
    }

    for (let iter = 0; iter < iterations; iter++) {
        // Assign colors to nearest centroid
        const clusters: RGB[][] = Array.from({ length: k }, () => []);

        for (const color of colors) {
            let minDist = Infinity;
            let nearestIdx = 0;

            for (let i = 0; i < centroids.length; i++) {
                const dist = colorDistance(color, centroids[i]);
                if (dist < minDist) {
                    minDist = dist;
                    nearestIdx = i;
                }
            }

            clusters[nearestIdx].push(color);
        }

        // Update centroids
        for (let i = 0; i < k; i++) {
            if (clusters[i].length > 0) {
                centroids[i] = {
                    r: clusters[i].reduce((sum, c) => sum + c.r, 0) / clusters[i].length,
                    g: clusters[i].reduce((sum, c) => sum + c.g, 0) / clusters[i].length,
                    b: clusters[i].reduce((sum, c) => sum + c.b, 0) / clusters[i].length,
                };
            }
        }
    }

    return centroids;
}

/**
 * Extract dominant colors from an image
 */
export async function extractColorsFromImage(
    imageUrl: string,
    numColors: number = 6,
    quality: number = 10 // Sample every nth pixel for performance
): Promise<string[]> {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'Anonymous';

        img.onload = () => {
            try {
                // Create canvas
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');

                if (!ctx) {
                    reject(new Error('Could not get canvas context'));
                    return;
                }

                // Scale down for performance
                const maxSize = 200;
                const scale = Math.min(maxSize / img.width, maxSize / img.height, 1);
                canvas.width = img.width * scale;
                canvas.height = img.height * scale;

                // Draw image
                ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

                // Get pixel data
                const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
                const pixels = imageData.data;

                // Collect colors
                const colors: RGB[] = [];

                for (let i = 0; i < pixels.length; i += 4 * quality) {
                    const r = pixels[i];
                    const g = pixels[i + 1];
                    const b = pixels[i + 2];
                    const a = pixels[i + 3];

                    // Skip transparent pixels
                    if (a < 128) continue;

                    const color: RGB = { r, g, b };

                    // Skip very neutral colors for variety
                    if (!isNeutralColor(color, 20)) {
                        colors.push(color);
                    }
                }

                if (colors.length === 0) {
                    // If no vibrant colors, include neutrals
                    for (let i = 0; i < pixels.length; i += 4 * quality) {
                        colors.push({
                            r: pixels[i],
                            g: pixels[i + 1],
                            b: pixels[i + 2]
                        });
                    }
                }

                // Cluster colors using k-means
                const clusteredColors = kMeansClustering(colors, numColors * 2, 15);

                // Filter out near-duplicate colors
                const uniqueColors: RGB[] = [];
                for (const color of clusteredColors) {
                    const isDuplicate = uniqueColors.some(
                        existing => colorDistance(existing, color) < 40
                    );
                    if (!isDuplicate) {
                        uniqueColors.push(color);
                    }
                }

                // Sort by saturation/vibrancy (more saturated colors first)
                uniqueColors.sort((a, b) => {
                    const satA = Math.max(a.r, a.g, a.b) - Math.min(a.r, a.g, a.b);
                    const satB = Math.max(b.r, b.g, b.b) - Math.min(b.r, b.g, b.b);
                    return satB - satA;
                });

                // Convert to hex and take requested number
                const hexColors = uniqueColors
                    .slice(0, numColors)
                    .map(c => rgbToHex(c.r, c.g, c.b));

                resolve(hexColors);
            } catch (error) {
                reject(error);
            }
        };

        img.onerror = () => {
            reject(new Error('Failed to load image'));
        };

        // Handle base64 and URLs
        img.src = imageUrl;
    });
}

/**
 * Extract colors from an image file
 */
export async function extractColorsFromFile(
    file: File,
    numColors: number = 6
): Promise<string[]> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = async (e) => {
            try {
                const dataUrl = e.target?.result as string;
                const colors = await extractColorsFromImage(dataUrl, numColors);
                resolve(colors);
            } catch (error) {
                reject(error);
            }
        };

        reader.onerror = () => reject(new Error('Failed to read file'));
        reader.readAsDataURL(file);
    });
}
